// ArticleA2.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  primary: "#60a5fa",
  primary2: "#22d3ee",
};

const META = {
  id: "a2",
  title: "วางแผนทรัพย์สินก่อนการหย่า: ปกป้องสิทธิอย่างมืออาชีพ",
  category: "ครอบครัว",
  dateISO: "2025-07-12",
  readMinsFallback: 6,
  hero:
    "https://www.lawdepot.com/images/lawLibrary/Articles/are-divorce-records-public.jpg",
  excerpt:
    "เข้าใจสินสมรส–สินส่วนตัว หลักฐานที่ศาลดู การแบ่งทรัพย์/หนี้ และตัวอย่างข้อตกลงการหย่าที่ใช้จริง",
};

export default function ArticleA2() {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [readMins, setReadMins] = useState(META.readMinsFallback);
  const contentRef = useRef(null);
  const heroRef = useRef(null);

  // progress + hero parallax
  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const total = el.scrollHeight - window.innerHeight * 0.6;
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - window.innerHeight * 0.4), 0),
        total
      );
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      const h = heroRef.current;
      if (h) {
        const y = window.scrollY;
        h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
        h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
        h.style.setProperty("--fade", Math.min(y / 420, 0.5));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // compute reading time
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const words = (el.innerText || "").trim().split(/\s+/).length;
    setReadMins(Math.max(4, Math.round(words / 200)));
  }, []);

  // ScrollSpy
  useEffect(() => {
    const heads = document.querySelectorAll("main article h2[id], main article h3[id]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id)),
      { rootMargin: "0px 0px -70% 0px", threshold: 0.01 }
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);

  const url = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : "/articles/a2"),
    []
  );

  const share = (type) => {
    const text = encodeURIComponent(META.title);
    const u = encodeURIComponent(url);
    let shareUrl = "";
    if (type === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (type === "tw") shareUrl = `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    if (type === "ln") shareUrl = `https://social-plugins.line.me/lineit/share?url=${u}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=680,height=520");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert("คัดลอกลิงก์แล้ว");
    } catch {
      alert("คัดลอกไม่สำเร็จ");
    }
  };
  const copyTemplate = () => {
    const t = document.getElementById("divorce-agreement-template")?.innerText || "";
    navigator.clipboard
      .writeText(t)
      .then(() => alert("คัดลอกแบบร่างข้อตกลงการหย่าแล้ว"))
      .catch(() => alert("คัดลอกไม่สำเร็จ"));
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  return (
    <div className="article-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      <ReadingProgress value={progress} />

      {/* SEO JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: META.title,
            datePublished: META.dateISO,
            dateModified: META.dateISO,
            author: { "@type": "Person", name: "ทีมทนายความ Thai Tax & Law" },
            image: META.hero,
            articleSection: META.category,
            description: META.excerpt,
            mainEntityOfPage: url,
          }),
        }}
      />

      {/* HERO */}
      <header className="hero" ref={heroRef}>
        <img className="hero-media" src={META.hero} alt="" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <a href="/articles">บทความ</a>{" "}
            <span>•</span> <span aria-current="page">{META.category}</span>
          </nav>
          <h1 className="hero-title">{META.title}</h1>
          <p className="hero-excerpt">{META.excerpt}</p>
          <div className="hero-meta">
            <span>{fmtDate(META.dateISO)}</span>
            <span>•</span>
            <span>{readMins} นาที</span>
            <span className="cat">{META.category}</span>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      {/* LAYOUT */}
      <div className="containerr">
        {/* SIDE */}
        <aside className="side tools">
          <div className="tool-card">
            <div className="tool-title">สารบัญ</div>
            <nav className="toc">
              {[
                ["intro", "ภาพรวมก่อนตัดสินใจ"],
                ["law", "พื้นฐานกฎหมาย: สินสมรส/สินส่วนตัว"],
                ["docs", "รวบรวมหลักฐานอะไรบ้าง"],
                ["paths", "ทางเลือก: จดการหย่าที่อำเภอ vs ฟ้องศาล"],
                ["agreement", "ตัวอย่างข้อตกลงการหย่า (ก๊อปได้)"],
                ["children", "บุตร: อำนาจปกครอง & ค่าอุปการะ"],
                ["house", "บ้าน/คอนโด & หุ้นธุรกิจ"],
                ["privacy", "ความเป็นส่วนตัว & ดิจิทัลไฮยีน"],
                ["whenlawyer", "เมื่อไรควรคุยกับทนายความ"],
                ["faq", "FAQ"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeId === id ? "active" : ""}
                  onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                    const yOffset = -80; // ชดเชย navbar 80px
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                }}

                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          

          
        </aside>

        {/* MAIN */}
        <main>
          <article ref={contentRef}>
            <p className="lede">
              เป้าหมายของบทความนี้คือช่วยให้คุณ <b>เตรียมทรัพย์สินและหลักฐาน</b> ให้พร้อม
              เพื่อเจรจาอย่างเป็นธรรม หรือเข้าสู่กระบวนการศาลอย่างมั่นใจ
              โดยอ้างอิงหลักกฎหมายครอบครัวไทย
            </p>

            <Callout type="info">
              บทความนี้เป็นข้อมูลทั่วไป ไม่ใช่คำปรึกษาเฉพาะเคส
              รายละเอียดของแต่ละครอบครัวมีความซับซ้อนแตกต่างกัน
              หากมีทรัพย์สินมูลค่าสูง/ธุรกิจร่วม/มีบุตร <a href="/contact">แนะนำปรึกษาทนายความ</a> ก่อนตัดสินใจ
            </Callout>

            <h2 id="intro">ภาพรวมก่อนตัดสินใจ</h2>
            <ul>
              <li>จดบันทึกทรัพย์สิน/หนี้ ณ ปัจจุบันและย้อนกลับไป 1–3 ปี</li>
              <li>ประเมินว่ามีเรื่องใด “ตกลงกันได้” และเรื่องใด “ต้องให้ศาลตัดสิน”</li>
              <li>ตั้งเป้าหมายที่ชัดเจน: ความปลอดภัยของบุตร/ความมั่นคงทางการเงิน/ความเป็นส่วนตัว</li>
              <li>สื่อสารอย่างมืออาชีพ เก็บหลักฐานทุกการสนทนา</li>
            </ul>

            <h2 id="law">พื้นฐานกฎหมาย: สินสมรส/สินส่วนตัว</h2>
            <div className="grid-2">
              <div>
                <h3>สินสมรส (ทรัพย์สินร่วม)</h3>
                <ul className="check">
                  <li>ทรัพย์ที่ได้มาระหว่างสมรส (เช่น เงินเดือน ผลกำไร)</li>
                  <li>ดอกผลของสินส่วนตัว (ดอกเบี้ย/ค่าเช่า)</li>
                  <li>หนี้เพื่อครอบครัวหรือประกอบอาชีพร่วม</li>
                </ul>
              </div>
              <div>
                <h3>สินส่วนตัว</h3>
                <ul className="check">
                  <li>ทรัพย์ที่มีอยู่ก่อนสมรส</li>
                  <li>ทรัพย์ที่ได้มาโดยรับมรดก/ให้โดยเสน่หา “เฉพาะตัว”</li>
                  <li>เครื่องใช้ส่วนตัว/ของหมั้น</li>
                </ul>
              </div>
            </div>
            <p className="muted">
              หลักทั่วไปคือ <b>แบ่งสินสมรสคนละครึ่ง</b> เว้นแต่พิสูจน์ได้ว่าเป็นสินส่วนตัว
              หรือมีส่วนได้เสียไม่เท่ากันจากเหตุพิเศษ (ขึ้นกับดุลพินิจศาลและพยานหลักฐาน)
            </p>

            <h2 id="docs">รวบรวมหลักฐานอะไรบ้าง</h2>
            <div className="grid-2">
              <ul className="check">
                <li>สมุดบัญชี/สเตทเมนต์ 12–24 เดือน</li>
                <li>โฉนด/เอกสารสิทธิอสังหาฯ พร้อมวันที่โอน</li>
                <li>ทะเบียนการค้า/ผู้ถือหุ้น/งบการเงิน (กรณีมีธุรกิจ)</li>
                <li>สัญญากู้/ใบเสร็จ/หลักฐานชำระหนี้</li>
              </ul>
              <ul className="check">
                <li>ข้อความ/อีเมลการตกลงที่เกี่ยวกับเงิน/บุตร</li>
                <li>หลักฐานการดูแลบุตร (ตารางเวลา ค่าใช้จ่าย)</li>
                <li>หลักฐานทรัพย์ส่วนตัว (มรดก/ของขวัญเฉพาะบุคคล)</li>
                <li>ทรัพย์ที่มีความเสี่ยงถูกโอนย้ายผิดปกติ</li>
              </ul>
            </div>

            <Callout type="tip">
              จัดแฟ้มดิจิทัลเป็นโฟลเดอร์: <b>Assets / Debts / Children / Business / Evidence</b>{" "}
              ตั้งชื่อไฟล์เป็นมาตรฐาน เช่น <code>20250712_bank_statement_kbank.pdf</code>
            </Callout>

            <h2 id="paths">ทางเลือก: จดการหย่าที่อำเภอ vs ฟ้องศาล</h2>
            <div className="grid-2">
              <div>
                <h3>จดการหย่าที่อำเภอ (ฉันทามติ)</h3>
                <ul>
                  <li>ใช้ได้เมื่อคู่สมรส “ตกลงเงื่อนไขทุกเรื่อง”</li>
                  <li>ควรมี <b>ข้อตกลงการหย่า</b> เป็นลายลักษณ์อักษรแนบท้าย</li>
                  <li>รวดเร็ว/ค่าใช้จ่ายต่ำ/ความสัมพันธ์ดี</li>
                </ul>
              </div>
              <div>
                <h3>ฟ้องศาล</h3>
                <ul>
                  <li>กรณีตกลงกันไม่ได้/มีข้อพิพาทเรื่องบุตร/ทรัพย์</li>
                  <li>ต้องเตรียมพยานหลักฐานและพยานบุคคล</li>
                  <li>ใช้เวลาและค่าใช้จ่ายมากกว่า แต่คุ้มเมื่อมูลค่าสูง/มีความเสี่ยง</li>
                </ul>
              </div>
            </div>

            <h2 id="agreement">ตัวอย่างข้อตกลงการหย่า (ก๊อปได้)</h2>
            <div className="template">
              <pre id="divorce-agreement-template">{`ข้อตกลงการหย่า (ฉบับร่าง)

ระหว่าง  นาย/นาง/น.ส. __________________ (ต่อไปนี้เรียกว่า “คู่สมรสฝ่ายที่ 1”)
และ      นาย/นาง/น.ส. __________________ (ต่อไปนี้เรียกว่า “คู่สมรสฝ่ายที่ 2”)

1) สถานภาพสมรส
คู่สัญญาตกลงเลิกการเป็นสามีภริยากันโดยสมัครใจ และจะไปดำเนินการจดทะเบียนการหย่าที่อำเภอ __________ ภายใน ____ วันนับแต่ลงนามในข้อตกลงนี้

2) ทรัพย์สินและหนี้สิน
2.1 สินสมรส: คู่สัญญาตกลงแบ่งทรัพย์สินร่วมดังต่อไปนี้
    - เงินฝาก/กองทุน/หลักทรัพย์: แบ่งคนละครึ่ง ณ วันที่ __________
    - อสังหาริมทรัพย์ __________ เลขที่โฉนด __________ จัดสรรให้ฝ่าย _______
    - ทรัพย์อื่น ๆ: __________________________
2.2 หนี้สิน: คู่สัญญาตกลงรับผิดชอบดังนี้
    - สัญญากู้เลขที่ __________ ธนาคาร __________  ฝ่าย _______ รับผิดชอบ
    - หนี้อื่น ๆ: __________________________

3) บุตรโดยชอบด้วยกฎหมาย
3.1 อำนาจปกครอง: คู่สัญญาตกลงใช้อำนาจปกครองร่วม/ฝ่าย _______ เป็นผู้ใช้อำนาจหลัก
3.2 การอยู่ร่วมกัน/เยี่ยมบุตร: กำหนดตารางเวลา __________
3.3 ค่าอุปการะเลี้ยงดู: ฝ่าย _______ ชำระเดือนละ _______ บาท โอนภายในทุกวันที่ ___
    และปรับตามดัชนีค่าครองชีพปีละ ____%

4) ค่าใช้จ่ายอื่น
ค่ารักษาพยาบาล/การศึกษา/กิจกรรมบุตร ให้รับผิดชอบตามสัดส่วน _______

5) ความลับและความเป็นส่วนตัว
คู่สัญญาตกลงไม่เปิดเผยข้อมูลส่วนตัว/ทางธุรกิจของอีกฝ่ายต่อสาธารณะ

6) การระงับข้อพิพาท
หากมีข้อพิพาทจากข้อตกลงนี้ ให้เจรจาไกล่เกลี่ยก่อน และหากตกลงไม่ได้ให้ยื่นต่อศาลที่มีอำนาจ

ลงชื่อ _______________________    คู่สมรสฝ่ายที่ 1        วันที่ __________
ลงชื่อ _______________________    คู่สมรสฝ่ายที่ 2        วันที่ __________`}</pre>
              <div className="template-actions">
                <button onClick={copyTemplate}>คัดลอกแบบร่าง</button>
                <a className="ghost" href="/contact">อยากให้เราปรับเป็นกรณีของคุณ</a>
              </div>
            </div>

            <h2 id="children">บุตร: อำนาจปกครอง & ค่าอุปการะ</h2>
            <ul>
              <li>
                หลักคือ <b>ผลประโยชน์สูงสุดของเด็ก</b>: ความต่อเนื่องในการเลี้ยงดู,
                ความพร้อมของผู้ปกครอง, สุขภาพ/การศึกษา
              </li>
              <li>
                การชำระค่าอุปการะ: ทำสัญญาเป็นลายลักษณ์อักษร ระบุวันชำระ ช่องทาง และหลักฐาน
              </li>
              <li>
                ตารางเยี่ยม/พำนัก: ทำเป็นปฏิทิน (เช่น สัปดาห์เว้นสัปดาห์/เสาร์–อาทิตย์/วันหยุดยาวสลับปี)
              </li>
            </ul>

            <h2 id="house">บ้าน/คอนโด & หุ้นธุรกิจ</h2>
            <div className="grid-2">
              <div>
                <h3>อสังหาริมทรัพย์</h3>
                <ul>
                  <li>ตรวจวันที่ซื้อ/แหล่งเงิน ถ้าใช้เงินสินสมรส → นับเป็นสินสมรส</li>
                  <li>วิธีจัดการ: โอนให้ฝ่ายใดฝ่ายหนึ่งพร้อมชดเชยมูลค่า หรือขายแล้วแบ่ง</li>
                  <li>เตรียมเอกสารธนาคารเรื่องปลดจำนอง/โอนหนี้กรณีมีสินเชื่อ</li>
                </ul>
              </div>
              <div>
                <h3>ธุรกิจ/หุ้น</h3>
                <ul>
                  <li>เช็คสัดส่วนผู้ถือหุ้น/มูลค่าโดยประมาณ/เงินปันผล</li>
                  <li>การชดเชยมูลค่าอาจทำได้ด้วยเงินสด/โอนหุ้น/แบ่งปันผลระยะเวลาหนึ่ง</li>
                  <li>ระวัง covenant/สัญญาหุ้นส่วน/SHA ที่มีข้อจำกัดการโอน</li>
                </ul>
              </div>
            </div>

            <h2 id="privacy">ความเป็นส่วนตัว & ดิจิทัลไฮยีน</h2>
            <ul className="no">
              <li>เปลี่ยนรหัสผ่านอีเมล/โซเชียล/ธนาคาร เปิด 2FA ทุกบัญชี</li>
              <li>สำรองข้อมูลเอกสารสำคัญแยกเก็บในคลาวด์ของตนเอง</li>
              <li>หยุดใช้เครื่อง/อีเมลที่ใช้งานร่วมกันในการคุยเรื่องการเงิน/ทนายความ</li>
            </ul>

            <h2 id="whenlawyer">เมื่อไรควรคุยกับทนายความ</h2>
            <ul>
              <li>มีทรัพย์สินมูลค่าสูง/ธุรกิจร่วม/หนี้จำนวนมาก</li>
              <li>คู่สมรสไม่ให้ความร่วมมือ/ปิดบังข้อมูล/โอนทรัพย์</li>
              <li>มีข้อพิพาทเรื่องบุตรหรือมีความรุนแรงในครอบครัว</li>
            </ul>

            <h2 id="faq">FAQ</h2>
            <details>
              <summary>ทรัพย์ที่พ่อแม่ให้หลังแต่งงานเป็นของใคร?</summary>
              <p>ถ้าให้ “เฉพาะตัว” ถือเป็นสินส่วนตัว แต่ถ้าให้เพื่อครอบครัว/ร่วมกัน อาจเป็นสินสมรส</p>
            </details>
            <details>
              <summary>หนี้บัตรเครดิตของคู่สมรส อีกฝ่ายต้องรับผิดไหม?</summary>
              <p>ถ้าเป็นหนี้เพื่อครอบครัว/ประกอบอาชีพร่วมกันอาจนับเป็นหนี้ร่วม แต่หนี้ส่วนตัวเฉพาะบุคคลมักเป็นความรับผิดเฉพาะผู้ก่อหนี้</p>
            </details>
            <details>
              <summary>ตกลงกันได้บางเรื่อง ต้องไปศาลไหม?</summary>
              <p>ทำข้อตกลงเฉพาะส่วนที่ตกลงกันและไปจดการหย่าที่อำเภอได้ ส่วนที่เหลือให้ศาลตัดสิน (กรณีจำเป็น)</p>
            </details>

            <Callout type="success">
              ต้องการทีมวางแผนและร่างข้อตกลงให้เหมาะกับกรณีของคุณ?{" "}
              <a href="/contact">นัดคุยฟรี 15 นาที</a> กับทีมทนายความคดีครอบครัวของเรา
            </Callout>

            <div className="end-actions">
              <a className="btn-primary" href="/contact">
                ปรึกษาทนายความตอนนี้
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 12h12M13 5l7 7-7 7" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                className="ghost"
                href="#top"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              >
                กลับขึ้นบน
              </a>
            </div>
          </article>

          {/* RELATED */}
          <section className="related">
            <h3>อ่านต่อ</h3>
            <div className="rel-grid">
              <a className="rel-card" href="/articles/a6">
                <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ครอบครัว</span>
                  <div className="title">ข้อตกลงก่อนสมรส (Prenup) จำเป็นเมื่อไร?</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a1">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ทวงหนี้</span>
                  <div className="title">คู่มือการติดตามทวงหนี้อย่างถูกกฎหมาย</div>
                </div>
              </a>
              <a className="rel-card" href="/articles/a11">
                <img src="https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta">
                  <span className="cat">ครอบครัว</span>
                  <div className="title">การหย่าด้วยความเข้าใจ: ขั้นตอนและเวลาโดยประมาณ</div>
                </div>
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ---------- Components ---------- */
function ReadingProgress({ value }) {
  return (
    <div className="progress-wrap" aria-hidden="true">
      <div className="progress" style={{ width: `${value}%` }} />
    </div>
  );
}
function Callout({ type = "info", children }) {
  return <div className={`callout ${type}`}>{children}</div>;
}

/* ---------- Styles ---------- */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg};
  --text:${THEME.textOnDark};
  --glass:${THEME.glass};
  --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.primary};
  --p2:${THEME.primary2};
}
*{box-sizing:border-box}
.article-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}

/* progress */
.progress-wrap{position:fixed;top:0;left:0;right:0;height:3px;z-index:100}
.progress{height:3px;background:linear-gradient(90deg,var(--p1),var(--p2));box-shadow:0 0 16px rgba(34,211,238,.35)}

/* hero */
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-excerpt{max-width:60ch;opacity:.98}
.hero-meta{display:flex;gap:8px;align-items:center;opacity:.9}
.hero-meta .cat{margin-left:8px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.2)}

/* layout */
.containerr{display:grid;grid-template-columns: 300px 1fr;gap:24px;max-width:1200px;margin:0 auto;padding: 0 clamp(16px,4vw,24px)}
.side{padding-top:28px}
main{padding-top:28px}

/* tools */
.tool-card{position:sticky;top:84px;background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);border-radius:16px;padding:14px 14px 12px;margin-bottom:14px}
.tool-title{font-weight:800;margin-bottom:8px}
.toc{display:grid;gap:8px}
.toc a{padding:8px 10px;border-radius:12px;text-decoration:none;color:var(--text);border:1px solid transparent}
.toc a:hover{background:rgba(255,255,255,.08)}
.toc a.active{border-color:rgba(255,255,255,.25);background:linear-gradient(90deg, rgba(96,165,250,.28), rgba(34,211,238,.28))}
.share .share-row{display:grid;gap:8px}
.share button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:var(--text);cursor:pointer}
.share button:hover{background:rgba(255,255,255,.12)}
.author .author-row{display:flex;gap:8px;align-items:center}
.author img{width:40px;height:40px;border-radius:50%;object-fit:cover}
.author .name{font-weight:700}
.author-meta{opacity:.85;margin-top:6px}

/* article */
article{max-width:760px;margin:0 auto 36px}
.lede{font-size:1.1rem;opacity:.98}
h2{font-size:1.6rem;margin:28px 0 12px;font-weight:900}
h3{font-size:1.15rem;margin:14px 0 6px;font-weight:800}
p{line-height:1.8}
ul{padding-left:18px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check li::marker{content:"✔  "}
.no li::marker{content:"✖  "}

/* callout */
.callout{border-radius:16px;padding:14px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);margin:14px 0}
.callout.info{background:linear-gradient(180deg, rgba(96,165,250,.15), rgba(255,255,255,.06))}
.callout.tip{background:linear-gradient(180deg, rgba(34,211,238,.15), rgba(255,255,255,.06))}
.callout.success{background:linear-gradient(180deg, rgba(52,211,153,.18), rgba(255,255,255,.06))}

/* template */
.template{border:1px solid var(--glass-stroke);border-radius:16px;overflow:hidden;background:rgba(255,255,255,.04);margin-top:8px}
.template pre{margin:0;padding:14px;line-height:1.7;white-space:pre-wrap}
.template-actions{display:flex;gap:10px;align-items:center;padding:10px;border-top:1px solid var(--glass-stroke)}
.template-actions .ghost{color:var(--text);opacity:.9;text-decoration:none}
.template-actions button{padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.08);color:var(--text);cursor:pointer}
.template-actions button:hover{background:rgba(255,255,255,.14)}

/* end actions */
.end-actions{display:flex;gap:10px;align-items:center;margin:18px 0 6px}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;background:linear-gradient(90deg,var(--p1),var(--p2));border:1px solid rgba(255,255,255,.25)}
.ghost{padding:10px 12px;border-radius:12px;border:1px solid var(--glass-stroke);text-decoration:none;color:var(--text);background:rgba(255,255,255,.06)}

/* related */
.related{border-top:1px solid rgba(255,255,255,.08);padding-top:20px}
.related h3{font-size:1.2rem;margin:0 0 12px}
.rel-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.rel-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(255,255,255,.12);text-decoration:none;color:#fff;background:#0f1629}
.rel-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9);transform:scale(1.02);transition:transform .5s}
.rel-card:hover img{transform:scale(1.06)}
.rel-card .meta{position:relative;padding:12px;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75));margin-top:110px}
.rel-card .cat{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);margin-bottom:6px}
.rel-card .title{font-weight:800}

/* responsive */
@media (max-width: 1024px){
  .containerr{grid-template-columns:1fr}
  .side{order:2}
  article{max-width:680px}
}
@media (max-width:640px){
  .hero{height:54vh}
  .grid-2{grid-template-columns:1fr}
  .rel-grid{grid-template-columns:1fr}
}
    `}</style>
  );
}
